import {
  create,
  fromJson,
  toJson,
  type DescMessage,
  type JsonValue,
  type MessageShape,
} from '@bufbuild/protobuf';
import { apiClient } from './request';
import {
  CreateTenantFailoverRequestSchema,
  CreateTenantFailoverResponseSchema,
  DeleteTenantFailoverRequestSchema,
  DeleteTenantFailoverResponseSchema,
  GetTenantFailoverRequestSchema,
  GetTenantFailoverResponseSchema,
  ListTenantFailoversResponseSchema,
  UpdateTenantFailoverRequestSchema,
  UpdateTenantFailoverResponseSchema,
  type CreateTenantFailoverRequest,
  type ListTenantFailoversData,
  type UpdateTenantFailoverRequest,
} from '@/proto/agentplatform/httpv2/tenant_failover_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { TenantFailover } from '@/proto/agentplatform/v2/tenant_failover_pb';

/** 发请求并按成功包络 schema 解析；业务错误已由 apiClient 拦截器统一 reject。 */
async function sendParsed<TResponse extends DescMessage>(
  path: string,
  body: JsonValue,
  successSchema: TResponse
): Promise<MessageShape<TResponse>> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  return fromJson(successSchema, responseObject.data);
}

/** 拆 httpv2 成功包络取业务 data，data 缺失视为异常。 */
async function send<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData> {
  const wrapped = await sendParsed(path, body, successSchema);
  const data = pickData(wrapped);
  if (data === undefined || data === null) {
    throw new Error('empty response data');
  }
  return data;
}

/** 同 send，但 data 允许为空（单例资源未配置时后端返回成功 + 空 data）。 */
async function sendOptional<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData | null> {
  const wrapped = await sendParsed(path, body, successSchema);
  return pickData(wrapped) ?? null;
}

/** 空 envelope 的成功响应（Update/Delete 无 data）；只做格式校验。 */
async function sendVoid<TResponse extends DescMessage>(
  path: string,
  body: JsonValue,
  successSchema: TResponse
): Promise<void> {
  await sendParsed(path, body, successSchema);
}

export const tenantFailoverApi = {
  list: async (params: ListRequest): Promise<ListTenantFailoversData> =>
    send(
      '/v2/ListTenantFailovers',
      toJson(ListRequestSchema, params),
      ListTenantFailoversResponseSchema,
      (r) => r.data
    ),

  get: async (): Promise<TenantFailover | null> =>
    sendOptional(
      '/v2/GetTenantFailover',
      toJson(GetTenantFailoverRequestSchema, create(GetTenantFailoverRequestSchema, {})),
      GetTenantFailoverResponseSchema,
      (r) => r.data
    ),

  create: async (data: CreateTenantFailoverRequest): Promise<TenantFailover> =>
    send(
      '/v2/CreateTenantFailover',
      toJson(CreateTenantFailoverRequestSchema, data),
      CreateTenantFailoverResponseSchema,
      (r) => r.data
    ),

  update: async (data: UpdateTenantFailoverRequest): Promise<void> =>
    sendVoid(
      '/v2/UpdateTenantFailover',
      toJson(UpdateTenantFailoverRequestSchema, data),
      UpdateTenantFailoverResponseSchema
    ),

  remove: async (id: string): Promise<void> =>
    sendVoid(
      '/v2/DeleteTenantFailover',
      toJson(DeleteTenantFailoverRequestSchema, create(DeleteTenantFailoverRequestSchema, { id })),
      DeleteTenantFailoverResponseSchema
    ),
};
